<template>
  <div class="reports-trips cols">
    <button class="green-gradient width-fit-content mt-5" @click="onNewsClick">
      {{ $t('reports.trips.eco-events.news-banner') }}
    </button>
    <div class="filter rows mobile-cols align-items-start">
      <div class="field-group cols align-items-start">
        <label for="vehicle">{{ $t('reports.trips.vehicle') }}</label>
        <ChooseVehicles id="vehicles"
                        ref="vehiclesSelect"
                        :preselectAll="false"
                        v-model="selectedVehicle"
                        :single-select="true"
                        :add-all-option="true"
                        :show-group-name="isGroupShown"
                        @update:modelValue="onSelectDeviceId">
        </ChooseVehicles>
      </div>
      <div class="field-group cols align-items-start">
        <label for="driver">{{ $t('reports.trips.result.driver') }}</label>
        <ChooseDriver id="driver"
                      v-model="selectedDriver"
                      :add-all-option="true"
                      @update:modelValue="onSelectDriver">
        </ChooseDriver>
      </div>
      <div class="field-group cols align-items-start" v-if="isGroupShown">
        <label for="group">{{ $t('generic.group') }}</label>
        <GroupsSelect id="group"
                      ref="groupsSelect"
                      v-model="tripsFilterGroup"
                      :add-all-option="true"
                      @update:modelValue="onSelectGroup">
        </GroupsSelect>
      </div>
      <div class="field-group cols align-items-start">
        <label for="dateFrom">{{ $t('reports.trips.date-from') }}</label>
        <Datepicker
            id="dateFrom"
            :model-value="tripsFilterDateFrom"
            @update:modelValue="onSelectDateFrom"
            :format="$t('settings.generic.date-time-format')"
            :locale="locale"
            :max-date="new Date()"
            auto-apply
        />
      </div>
      <div class="field-group cols align-items-start">
        <label for="dateTo">{{ $t('reports.trips.date-to') }}</label>
        <Datepicker
            id="dateTo"
            :model-value="tripsFilterDateTo"
            @update:modelValue="onSelectDateTo"
            :format="$t('settings.generic.date-time-format')"
            :locale="locale"
            :max-date="new Date()"
            auto-apply
        />
      </div>
      <div class="field-group cols align-items-start">
        <label>&nbsp;</label>
        <button type="submit" @click="search" :disabled="!canSearch">
          {{ $t('reports.trips.search') }}
        </button>
      </div>
      <div class="field-group cols align-items-start">
        <label>&nbsp;</label>
        <button @click="exportFile" :disabled="!canSearch">
          {{ $t('reports.trips.export') }}
        </button>
      </div>
    </div>
    <div class="pl-10 pt-10">
      <div>
        <b>{{ $t('reports.trips.result.odometer') }}</b>:
        {{ $t('reports.trips.result.odometer-start') }}: {{ formatKilometers(periodOdometer?.start) }} |
        {{ $t('reports.trips.result.odometer-end') }}: {{ formatKilometers(periodOdometer?.end) }} |
        {{ $t('reports.trips.result.odometer-total') }}: {{ formatKilometers(periodOdometer?.total) }}
      </div>
      <div v-if="showEngineHours">
        <b>{{ $t('dashboard.quick-map.info-window.engine-hours')}}</b>:
        {{ $t('reports.trips.result.engine-hours-start') }}: {{ formatEngineHours(periodTotals?.engineHours?.initial) }} |
        {{ $t('reports.trips.result.engine-hours-end') }}: {{ formatEngineHours(periodTotals?.engineHours?.end) }} |
        {{ $t('reports.trips.result.engine-hours-total') }}: {{ formatEngineHours(periodTotals?.engineHours?.total) }}
      </div>
      <div v-if="showMovingTime">
        <b>{{ $t('reports.trips.result.moving-time') }}</b>:
        {{ $t('reports.trips.result.moving-time-start') }}: {{ formatTimeWithSeconds(periodMovingTime?.firstMovingTime) }} |
        {{ $t('reports.trips.result.moving-time-end') }}: {{ formatTimeWithSeconds(periodMovingTime?.lastMovingTime) }} |
        {{ $t('reports.trips.result.moving-time-idle-total') }}: {{ formatDuration(periodMovingTime?.totalIdle) }} |
        {{ $t('reports.trips.result.moving-time-total') }}: {{ formatDuration(periodMovingTime?.total) }}
      </div>
    </div>
    <div class="result-set">
      <div v-if="!hasRecords" class="message info">
        {{ $t('reports.trips.result.empty') }}
      </div>
      <table v-else class="table-tracking">
        <thead>
        <tr>
          <th>&nbsp;</th>
          <th>{{ $t('reports.trips.result.date') }}</th>
          <th v-if="showVehicleColumn">{{ $t('task.vehicle') }}</th>
          <th>{{ $t('reports.trips.result.start-time') }}</th>
          <th>{{ $t('reports.trips.result.end-time') }}</th>
          <th>{{ $t('reports.trips.result.kilometers') }}</th>
          <th>{{ $t('reports.trips.result.average-speed') }}</th>
          <th>{{ $t('reports.trips.result.duration') }}</th>
          <th v-if="hasFuelData">{{ $t('reports.trips.result.fuel') }}</th>
          <th v-if="hasRefuelData">{{ $t('reports.trips.result.refuel') }}</th>
          <th v-if="showEngineHours" class="engine-hours-column">{{ $t('dashboard.quick-map.info-window.engine-hours') }}</th>
          <th v-if="hasFuelCapData">{{ $t('reports.trips.result.fuelCapOpen') }}</th>
          <th v-if="checkForDrivers(tripsRecords)">{{ $t('reports.trips.result.driver') }}</th>
          <th v-if="checkForErrors(tripsRecords)">{{ $t('reports.trips.result.OBD.faults') }}</th>
          <th>{{ $t('reports.trips.result.trips') }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(record, key) in tripsRecords" v-bind:key="key">
          <td>{{ key + 1 }}</td>
          <td>{{ formatDate(record.startTime) }}</td>
          <td v-if="showVehicleColumn">{{ getRecordVehicleName(record) }}</td>
          <td>
            {{ formatTime(record.startTime) }}
            <br>
            <a target="_blank" :href="getRouteLink(record.startCoordinates)">
              {{ getRecordStartAddress(record) }}
            </a>
          </td>
          <td>
            {{ formatTime(record.endTime) }}
            <br>
            <a target="_blank" :href="getRouteLink(record.endCoordinates)">
              {{ getRecordEndAddress(record) }}
            </a>
          </td>
          <td>
            {{ formatKilometers(record.distance['total']) }}
          </td>
          <td>
            {{ formatAverageSpeed(record.averageSpeed) }}
          </td>
          <td>
            {{ formatDuration(record.duration.total) }}
          </td>
          <td v-if="hasFuelData" class="text-center">
            {{ $t('reports.trips.result.fuelStart') }}:
            {{ formatFuel(record['initialFuel']) }}
            <br/>
            {{ $t('reports.trips.result.fuelEnd') }}:
            {{ formatFuel(record['endFuel']) }}
          </td>
          <td v-if="hasRefuelData">
            <div v-if="record['reFuel']['refuelLiters'] > 5">
              <button
                  class="transparent"
                  @click="formatRefuelLink(record['reFuel']['longitude'], record['reFuel']['latitude'])"
              >
                <img src="@/assets/images/Trips/fuelpump-icon.png" width="20" height="20">
              </button>
              {{ record['reFuel']['totalRefuelLiters'] }} (l)
            </div>
            <div v-else>-</div>
          </td>
          <td class="text-center" v-if="showEngineHours">
            {{ $t('reports.trips.result.engine-hours-start') }}:
            {{ formatEngineHours(record.engineHours.initial) }}
            <br/>
            {{ $t('reports.trips.result.engine-hours-end') }}:
            {{ formatEngineHours(record.engineHours.end) }}
            <br/>
            {{ $t('reports.trips.result.engine-hours-total') }}:
            {{ formatEngineHours(record.engineHours.total) }}
          </td>
          <td v-if="hasFuelCapData">
            <button
                v-if="record['fuelCapStatus'][0]"
                class="transparent"
                @click="formatFuelCapStatus(record['fuelCapStatus'][0]['lng'], record['fuelCapStatus'][0]['lat'])"
            >
              <img src="@/assets/images/Trips/fuelcap-icon.png" width="30" height="30">
            </button>
            <span v-else>-</span>
          </td>
          <td v-if="driver && record['driver'] !== 'no driver'">
            {{ record['driver'] }}
          </td>
<!--          <td v-else>-</td>-->
          <td v-if="record['errorCodes']" v-html="formatErrorCodeLinks(record['errorCodes'])">

          </td>
          <td>
            <button class="transparent table-action" @click="openEcoEvents(record)">
              <i class="bi bi-signpost-split-fill"></i>
            </button>
          </td>
        </tr>
        </tbody>
        <thead>
        <tr>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
          <th v-if="showVehicleColumn">&nbsp;</th>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
          <th>
            {{ formatKilometers(tripRecordsTotals.distance) }}
          </th>
          <th>
            {{ formatAverageSpeed(tripRecordsTotals.averageSpeed) }}
          </th>
          <th>
            {{ formatDuration(tripRecordsTotals.duration) }}
          </th>
          <th v-if="hasFuelData" class="text-center">
            {{ $t('reports.trips.result.used-fuel') }}
            {{ formatFuel(tripRecordsTotals.fuel) }}
          </th>
          <th v-if="hasRefuelData">&nbsp;</th>
          <th v-if="showEngineHours">
            {{ formatEngineHours(tripRecordsTotals.engineHours) }}
          </th>
          <th v-if="hasFuelCapData">&nbsp;</th>
          <th v-if="checkForErrors(tripsRecords)">&nbsp;</th>
          <th v-if="checkForDrivers(tripsRecords)">&nbsp;</th>
          <th>
            <button class="transparent table-action" @click="openAllEcoEvents()">
              <i class="bi bi-signpost-split-fill"></i>
            </button>
          </th>

        </tr>
        </thead>
      </table>
    </div>
  </div>
</template>

<script>
import Datepicker from '@vuepic/vue-datepicker';
import locale from '@/assets/js/locale';
import {mapGetters, mapState} from 'vuex';
import {dataManipulator, entityDataManager, GoogleMapsManager} from '@/assets/js/data-manipulation';
import {actions, mutations} from '@/assets/js/vue/vuex/actions';
import notification from '@/assets/js/notification';
import moment from "moment/moment";
import ChooseVehicles from "@/components/pages/partials/ChooseVehicles.vue";
import GroupsSelect from "@/components/pages/partials/GroupsSelect.vue";
import ChooseDriver from "@/components/pages/partials/ChooseDriver.vue";

export default {
  components: {
    ChooseDriver,
    GroupsSelect,
    ChooseVehicles,
    Datepicker,
  },
  name: 'Trips',
  data() {
    return {
      locale: locale.getLocale(),
      record: [],
      driver: false,
      selectedVehicle: null,
      selectedDriver: null,
      searchDriverId: null,
      showVehicleColumn: false,
      showEngineHours: false,
      showMovingTime: false
    };
  },
  computed: {
    ...mapState([
      'vehicles',
      'tripsFilterDateFrom',
      'tripsFilterDateTo',
      'tripsFilterDevice',
      'tripsFilterDriver',
      'tripsFilterGroup',
      'tripsData',
      'tripsMapData',
      'userGeoFences'
    ]),
    ...mapGetters(['isGroupShown', 'userId']),
    canSearch() {
      return Boolean(
          this.tripsFilterDateFrom &&
          this.tripsFilterDateTo &&
          (this.tripsFilterDevice || this.tripsFilterDriver)
      );
    },
    tripsRecords() {
      return this.tripsData?.trips ?? [];
    },
    tripVehicles() {
      const deviceIds = new Set(this.tripsRecords.map(record => record.deviceId));
      return this.vehicles.filter(vehicle => deviceIds.has(vehicle.deviceId));
    },
    hasRecords() {
      return this.tripsRecords.length > 0;
    },
    periodTotals() {
      return {
        engineHours: {
          initial: this.tripsData?.engineHours?.initial,
          end: this.tripsData?.engineHours?.end,
          total: this.tripsData?.engineHours?.total,
        },
        movingTime: this.tripsData?.movingTime,
        odometer: this.tripsData?.odometer,
      };
    },
    periodMovingTime() {
      return this.periodTotals.movingTime;
    },
    periodOdometer() {
      return this.periodTotals.odometer;
    },
    tripRecordsTotals() {
      const tripRecordTotals = {
        duration: 0,
        distance: 0,
        averageSpeed: 0,
        fuel: 0,
        reFuel: 0,
        engineHours: 0,
      };

      if (!this.hasRecords) {
        return tripRecordTotals;
      }

      this.tripsRecords.forEach(record => {
        tripRecordTotals.duration += record.duration['total'];
        tripRecordTotals.distance += record.distance['total'];
        tripRecordTotals.averageSpeed += record.averageSpeed;
        if (record['reFuel']['refuelLiters'] != null) {
          tripRecordTotals.reFuel += record['reFuel']['refuelLiters'];
        }

        if (this.searchDriverId) {
          const refuel = record.reFuel?.totalRefuelLiters ?? record.reFuel?.refuelLiters ?? 0;
          tripRecordTotals.fuel += (record.initialFuel + refuel) - record.endFuel;
          tripRecordTotals.engineHours += record.engineHours.end - record.engineHours.initial;
        }
      });

      tripRecordTotals.averageSpeed /= this.tripsRecords.length;

      if (!this.searchDriverId) {
        const firstTrip = this.tripsRecords[0];
        const lastTrip = this.tripsRecords[this.tripsRecords.length - 1];

        tripRecordTotals.fuel =(firstTrip['initialFuel']+tripRecordTotals.reFuel)-lastTrip['endFuel'];
        tripRecordTotals.engineHours = lastTrip.engineHours.end - firstTrip.engineHours.initial;
      }

      return tripRecordTotals;
    },
    hasFuelData() {
      return this.tripsRecords.some(item => item.initialFuel != null || item.endFuel != null);
    },
    hasRefuelData() {
      return this.tripsRecords.some(item => item.reFuel?.refuelLiters > 5);
    },
    hasFuelCapData() {
      return this.tripsRecords.some(item => item['fuelCapStatus']?.length > 0);
    },
  },
  mounted() {
    if (this.tripsFilterDevice !== null) {
      this.selectedVehicle = this.tripsFilterDevice;
    }

    if (this.tripsFilterDriver !== null) {
      this.selectedDriver = this.tripsFilterDriver;
    }

    if (!this.tripsFilterDateFrom) {
      this.onSelectDateFrom(moment().startOf('day').toDate());
    }

    if (!this.tripsFilterDateTo) {
      this.onSelectDateTo(moment().endOf('day').toDate());
    }

    if (this.$route.query.search === '1') {
      if (this.canSearch) {
        this.search();
      }
    }
    else if (this.hasRecords) {
      this.searchDriverId = this.tripsFilterDriver?.id ?? null;
      this.showVehicleColumn = this.shouldShowVehicleColumn(Boolean(this.tripsFilterDevice));
      this.setVehicleReportSettings();
    }
  },
  methods: {
    onSelectDateFrom(value) {
      this.$store.commit(
          mutations.SET_TRIPS_FILTER_DATE_FROM,
          {date: dataManipulator.dateTimeToString(value)},
      );
    },
    onSelectDateTo(value) {
      this.$store.commit(
          mutations.SET_TRIPS_FILTER_DATE_TO,
          {date: dataManipulator.dateTimeToString(value)},
      );
    },
    onSelectDeviceId(value) {
      this.$store.commit(mutations.SET_TRIPS_FILTER_DEVICE_ID, value);
    },
    onSelectDriver(value) {
      this.$store.commit(mutations.SET_TRIPS_FILTER_DRIVER, value);
    },
    onSelectGroup(value) {
      this.$store.commit(mutations.SET_TRIPS_FILTER_GROUP, value);
      if (this.$refs.groupsSelect.isAllSelected) {
        this.$refs.vehiclesSelect.clearGroupIdFilter();
      }
      else {
        this.$refs.vehiclesSelect.filterByGroupId(value.id);
      }
    },
    search() {
      const vehicleFilterSelected = Boolean(this.tripsFilterDevice);
      this.$store.commit(mutations.SET_TRIPS_FILTER_INCLUDE_ADDRESSES, true);
      this.$store.dispatch(actions.GET_TRIPS_DATA)
          .then(() => {
            this.searchDriverId = this.tripsFilterDriver?.id ?? null;
            this.showVehicleColumn = this.shouldShowVehicleColumn(vehicleFilterSelected);
            this.setVehicleReportSettings();
          })
          .catch(e => notification.error(this.$t(e.message)));
    },
    exportFile() {
      const vehicle = entityDataManager.getVehicleByDeviceId(this.vehicles, this.tripsFilterDevice?.deviceId);
      const filenameBase = vehicle?.licensePlate ?? this.tripsFilterDriver?.name ?? this.$t('navbar.reports.trips');
      this.$store.commit(mutations.SET_TRIPS_FILTER_INCLUDE_ADDRESSES, true);
      this.$store.dispatch(actions.EXPORT_TRIPS_DATA, {licensePlate: filenameBase}).
          catch(e => notification.error(this.$t(e.message)));
    },
    openEcoEvents(route) {
      const payload = {
          deviceId: route.deviceId,
          from: route.startTime,
          to: route.endTime,
          addresses: 0,
          includeMapData: 1,
        };

      this.loadTripsMapData(payload)
          .then(() => this.openEcoEventsWithTripsData())
          .catch(e => notification.error(this.$t(e.message)));
    },
    openAllEcoEvents() {
      this.loadTripsMapData({addresses: 0, includeMapData: 1})
          .then(() => this.openEcoEventsWithTripsData())
          .catch(e => notification.error(this.$t(e.message)));
    },
    getTripsMapCacheKey(payload) {
      return JSON.stringify({
        from: payload.from ?? this.tripsFilterDateFrom,
        to: payload.to ?? this.tripsFilterDateTo,
        deviceId: payload.deviceId ?? this.tripsFilterDevice?.deviceId ?? null,
        driverId: payload.driverId ?? this.tripsFilterDriver?.id ?? null,
      });
    },
    loadTripsMapData(payload) {
      const key = this.getTripsMapCacheKey(payload);
      if (this.tripsMapData?.key === key) {
        return Promise.resolve(this.tripsMapData.data);
      }

      return this.$store.dispatch(actions.GET_TRIPS_DATA, payload)
          .then(data => {
            this.$store.commit(mutations.SET_TRIPS_MAP_DATA, {key, data});

            return data;
          });
    },
    openEcoEventsWithTripsData() {
      this.$router.push({path: '/reports/eco-events', query: {tripsMap: 1}});
    },
    setVehicleReportSettings() {
      if (!this.tripVehicles.length) {
        this.showEngineHours = false;
        this.showMovingTime = false;
        return;
      }

      this.showEngineHours = this.tripVehicles.some(vehicle => vehicle.engineHoursSupported);
      this.showMovingTime = this.tripVehicles.some(vehicle => vehicle.movingTimeSupported);
    },
    shouldShowVehicleColumn(vehicleFilterSelected) {
      if (!vehicleFilterSelected) {
        return true;
      }

      const deviceIds = new Set(this.tripsRecords.map(record => record.deviceId));
      return deviceIds.size > 1;
    },
    formatDate(date) {
      return dataManipulator.iso8601DateStringToLocalMoment(date).
          format(this.$t('settings.generic.date-format-moment'));
    },
    formatTime(fromDate) {
      return dataManipulator.iso8601DateStringToLocalMoment(fromDate).format('HH:mm');
    },
    formatTimeWithSeconds(fromDate) {
      if (!fromDate) {
        return '-';
      }

      return dataManipulator.iso8601DateStringToLocalMoment(fromDate).format('HH:mm:ss');
    },
    formatErrorCodeLinks(errorCodes) {
      const errorCodesArray = errorCodes.split(' ');
      const errorCodesFinalArray = [];
      for (let i = 0;  i < errorCodesArray.length; i++){
        let currentError = errorCodesArray[i];
        const currentLink = `<a target="_blank" href="https://www.google.com/search?q=${currentError}+fault+code">${currentError}</a>`;
        errorCodesFinalArray.push(currentLink);
      }
      return errorCodesFinalArray.filter(item => item).join(' - ');
    },
    getRecordStartAddress(record) {
      let geofenceName = null;
      if (record.startGeofence != null) {
        const geofence = entityDataManager.getById(this.userGeoFences, record.startGeofence);
        if (geofence) {
          geofenceName = geofence?.name + ' - ' + geofence?.description;
        }
      }

      return geofenceName ?? record.startAddress;
    },
    getRecordEndAddress(record) {
      let geofenceName = null;
      if (record.endGeofence != null) {
        const geofence = entityDataManager.getById(this.userGeoFences, record.endGeofence);
        if (geofence) {
          geofenceName = geofence?.name + ' - ' + geofence?.description;
        }
      }

      return geofenceName ?? record.endAddress;
    },
    getRecordVehicleName(record) {
      const vehicle = entityDataManager.getVehicleByDeviceId(this.vehicles, record.deviceId);
      return vehicle?.fullName ?? '-';
    },
    getRouteLink(coordinates) {
      const latLon = coordinates.split(', ');
      return GoogleMapsManager.getLocationLink(latLon[0], latLon[1]);
    },
    formatRefuelLink(longitude, latitude) {
      GoogleMapsManager.openLocationLink(latitude, longitude);
    },
    formatFuelCapStatus(longitude, latitude) {
      GoogleMapsManager.openLocationLink(latitude, longitude);
    },
    formatKilometers(distanceInMeters) {
      if (distanceInMeters == null) {
        return '-';
      }

      return dataManipulator.truncateToDecimals(distanceInMeters / 1000, 2);
    },
    formatAverageSpeed(averageSpeed) {
      return dataManipulator.truncateToDecimals(dataManipulator.knotToKm(averageSpeed), 2);
    },
    formatDuration(milliseconds) {
      if (milliseconds == null) {
        return '-';
      }

      const time = dataManipulator.millisecondsToTime(milliseconds);
      return dataManipulator.formatDuration(time);
    },
    formatFuel(fuel) {
      return fuel ? fuel.toFixed(0) : '-';
    },
    formatEngineHours(milliseconds) {
      return entityDataManager.formatEngineHours(milliseconds);
    },
    checkForErrors(tripsRecords){
      for (let i = 0;  i < tripsRecords.length; i++){
        if (tripsRecords[i]['errorCodes']){
          return true;
        }
      }
      return false;
    },
    checkForDrivers(tripsRecords){
      for (let i = 0;  i < tripsRecords.length; i++) {

        if (tripsRecords[i]['driver'] !== "no driver") {
          this.driver = true
          return true;
        }
      }
        return false;
    },
    onNewsClick() {
      window.open('https://drive.google.com/file/d/19IxqYMwA8j6fiJmf0NARxuDi1msvr-Or/view?t=2', '_blank');
      this.$gtag.event('trips_news_click', {
        event_label: 'Trips News Click',
        event_category: 'Trips',
        user_id: `${this.userId}`,
        timestamp: new Date().toISOString(),
      });
    }
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';

.reports-trips {
  .filter {
    margin-top: 20px;
    padding: 10px;

    .field-group {
      margin-right: 5px;

      label {
        margin-bottom: 10px;
      }
    }
  }

  .engine-hours-column {
    width: 140px;
  }

  .result-set {
    padding: 10px;
  }
}

// Mobile:
@media (max-width: $breaking-point) {
  .reports-trips {
    .filter {
      .field-group {
        width: 100%;

        label {
          margin-top: 10px;
        }

        label, input, select, .multiselect, .dp__main {
          width: 100%;
        }
      }
    }
  }
}
</style>
